import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useHttp from 'app/utils/hooks/use-http';

import CreateAccountView from './CreateAccount.view';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import * as yup from 'yup';
import { FormikContextType, useFormik } from 'formik';

import * as authActions from '../../store/actions/auth';

import { LocalStorage } from 'app/constants/StorageConstants';

import { IUser } from 'app/models/user';
import { ISignInResponse } from 'app/models/response';
import { FormValidationObject } from 'app/models/general';
import { CreateAccountState, ISignInFields, ISignUpFields } from './CreateAccount.model';

interface PropsFromDispatch {
  login: (user: IUser) => void;
}

interface Props extends PropsFromDispatch {
  onClose: () => void;
}

const CreateAccount: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const http = useHttp();

  const [accountState, setAccountState] = useState<CreateAccountState>(CreateAccountState.Default);
  const [signInMessageState, setSignInMessagestate] = useState<string | null>(null);
  const [signUpMessageState, setSignUpMessagestate] = useState<string | null>(null);

  const handleContainerClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      props.onClose();
    }
  };

  //form for sign in
  const formSignIn: FormikContextType<ISignInFields> = useFormik<ISignInFields>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: yup.object<FormValidationObject<keyof ISignInFields>>({
      email: yup.string().required(t('createAccount.signIn.form.fields.email.required')),
      password: yup.string().required(t('createAccount.signIn.form.fields.password.required')),
    }),
    onSubmit(values: ISignInFields) {
      http.auth
        .signIn({ email: values.email, password: values.password })
        .then((response: ISignInResponse) => {
          localStorage.setItem(LocalStorage.Token, response.token);
          props.login(response.user as IUser);
          props.onClose();
        })
        .catch(() => {
          setSignInMessagestate(() => t('createAccount.signIn.serverMessage'));
        });
    },
  });

  //form for sign up
  const formSignUp: FormikContextType<ISignUpFields> = useFormik<ISignUpFields>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
    },
    validationSchema: yup.object<FormValidationObject<keyof ISignUpFields>>({
      firstName: yup.string().required(t('createAccount.signUp.form.fields.firstName.required')),
      lastName: yup.string().required(t('createAccount.signUp.form.fields.lastName.required')),
      email: yup.string().required(t('createAccount.signUp.form.fields.email.required')),
      phone: yup.string(),
      password: yup.string().required(t('createAccount.signUp.form.fields.password.required')),
    }),
    onSubmit(values: ISignUpFields) {
      setSignUpMessagestate(() => t('createAccount.signUp.serverMessages.exist'));
      console.log(values);
    },
  });

  const changeAccountState = useCallback(
    (state: CreateAccountState) => {
      switch (state) {
        case CreateAccountState.Default:
          setAccountState(() => CreateAccountState.Default);
          break;
        case CreateAccountState.SignIn:
          setAccountState(() => CreateAccountState.SignIn);
          break;
        case CreateAccountState.SignUp:
          setAccountState(() => CreateAccountState.SignUp);
          break;
      }

      formSignIn.resetForm();
      formSignUp.resetForm();
      setSignInMessagestate(() => null);
      setSignUpMessagestate(() => null);
    },
    [setAccountState, setSignInMessagestate],
  );

  return (
    <CreateAccountView
      account={accountState}
      formSignIn={formSignIn}
      formSignUp={formSignUp}
      errorSignInMessage={signInMessageState}
      errorSignUpMessage={signUpMessageState}
      onIconClick={props.onClose}
      onLogin={() => changeAccountState(CreateAccountState.SignIn)}
      onJoinUs={() => changeAccountState(CreateAccountState.Default)}
      onSignUp={() => changeAccountState(CreateAccountState.SignUp)}
      onContainerClick={handleContainerClick}
    >
      {props.children}
    </CreateAccountView>
  );
};

CreateAccount.displayName = 'CreateAccount';
CreateAccount.defaultProps = {};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => {
  return { login: (user: IUser): authActions.Login => dispatch(authActions.login(user)) };
};

export default connect(null, mapDispatchToProps)(CreateAccount);
